import React, { useState, useEffect, useRef } from 'react';
import { Box, Skeleton } from '@mui/material';
import { logger } from '../utils/logger';
import { isPresignedUrl, generateCdnUrl, generateSrcSet } from '../utils/imageUtils';
import { useImageRetry } from '../hooks/useImageRetry';
import { sendImageErrorBeacon } from '../utils/imageErrorBeacon';
import { NoImagePlaceholder } from './NoImagePlaceholder';

interface OptimizedImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  onLoad?: () => void;
  onError?: () => void;
  className?: string;
  style?: React.CSSProperties;
}

interface FormatSupport {
  avif: boolean;
  webp: boolean;
}

const detectFormatSupport = (): FormatSupport => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;

    const avifData = canvas.toDataURL('image/avif');
    const webpData = canvas.toDataURL('image/webp');

    return {
      avif: avifData && avifData.indexOf('data:image/avif') === 0,
      webp: webpData && webpData.indexOf('data:image/webp') === 0,
    };
  } catch (error) {
    // Fallback for test environments or unsupported browsers
    logger.debug('Failed to detect image format support', error);
    return {
      avif: false,
      webp: false,
    };
  }
};

export const OptimizedImage: React.FC<OptimizedImageProps> = ({
  src,
  alt,
  width,
  height,
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  loading = 'lazy',
  onLoad,
  onError,
  className,
  style
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [formatSupport] = useState<FormatSupport>(() => detectFormatSupport());
  const [isInView, setIsInView] = useState(loading !== 'lazy');
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // For presigned URLs or blob URLs, use them directly without optimization
  const isPresigned = isPresignedUrl(src);

  const optimizedSrc = isPresigned ? src : (
    formatSupport.avif
      ? generateCdnUrl(src, 'avif')
      : formatSupport.webp
      ? generateCdnUrl(src, 'webp')
      : generateCdnUrl(src, 'jpg')
  );

  const { currentSrc, hasExhaustedRetries, handleError: retryHandleError, handleLoad: retryHandleLoad } = useImageRetry({
    src: optimizedSrc,
    onAllRetriesFailed: (details) => {
      sendImageErrorBeacon({
        url: details.originalUrl,
        retryCount: details.retryCount,
      });
    },
  });

  const srcSet = isPresigned ? undefined : generateSrcSet(src, formatSupport);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading !== 'lazy' || isInView) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observerRef.current?.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observerRef.current.observe(imgRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [loading, isInView]);

  // Debug logging for image source (dev only)
  useEffect(() => {
    logger.debug('[IMAGE_DEBUG] OptimizedImage rendering', {
      src: src.substring(0, 100) + (src.length > 100 ? '...' : ''),
      isPresignedUrl: isPresigned,
      isInView,
      isLoading,
      hasExhaustedRetries
    });
  }, [src, isPresigned, isInView, isLoading, hasExhaustedRetries]);

  // Timeout detection for stuck loading state (5s for better UX feedback)
  useEffect(() => {
    if (!isLoading || hasExhaustedRetries) return;

    const timeout = setTimeout(() => {
      logger.warn('[IMAGE_DEBUG] Image stuck in loading state after 5s', {
        src: src.substring(0, 100) + (src.length > 100 ? '...' : ''),
        isInView,
        isLoading,
        hasExhaustedRetries
      });
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isLoading, hasExhaustedRetries, src, isInView]);

  const handleLoad = () => {
    logger.debug('[IMAGE_DEBUG] Image loaded successfully', src.substring(0, 100));
    setIsLoading(false);
    retryHandleLoad();
    onLoad?.();
  };

  const handleError = () => {
    logger.error('[IMAGE_DEBUG] Image load error', src.substring(0, 100));
    retryHandleError();
    onError?.();
  };

  if (hasExhaustedRetries) {
    return (
      <Box sx={{ width: width || '100%', height: height || 200 }}>
        <NoImagePlaceholder variant="detail" />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: width || '100%', height: height || 'auto' }}>
      {isLoading && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
        />
      )}
      <img
        ref={imgRef}
        src={currentSrc}
        srcSet={srcSet}
        sizes={sizes}
        alt={alt}
        crossOrigin={isPresigned ? undefined : "anonymous"}
        onLoad={handleLoad}
        onError={handleError}
        className={className}
        style={{
          ...style,
          width: '100%',
          height: 'auto',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.3s ease-in-out',
          objectFit: 'contain',
          objectPosition: 'center',
          display: 'block'
        }}
        loading={loading}
        aria-label={alt}
      />
    </Box>
  );
};
